import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
} from 'react';
import { BASE_NAME, ENV_TYPE } from '../utils/helpers';

interface ThemeContextType {
  currentTheme: string;
  setTheme: (theme: string) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  currentTheme: 'version1',
  setTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: React.ReactNode;
  // #72: a theme decided by the entry itself (full-screen QR): it is neither
  // read from nor written to the storage shared with other tabs
  fixedTheme?: string;
  // #151: the theme decided by the entry URL of an ordinary (embedded) entry;
  // resolved once with fixedTheme (fixedTheme ?? entryTheme ?? stored ??
  // version1) in the state initialiser, so the first render is already right.
  // Both props are mount-only inputs: a later change is ignored (remount to
  // resolve a new entry theme).
  entryTheme?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  fixedTheme,
  entryTheme,
}) => {
  const [currentTheme, setCurrentTheme] = useState(
    () =>
      fixedTheme ??
      entryTheme ??
      (localStorage.getItem('appTheme') || 'version1')
  );
  // decided once per entry
  const fixedEntryTheme = useRef(fixedTheme);

  useEffect(() => {
    applyTheme(currentTheme);
  }, []);

  const applyTheme = (themeName: string) => {
    // Remove existing theme stylesheets
    const existingThemes = document.querySelectorAll('link[data-theme]');
    existingThemes.forEach(link => link.remove());

    // Add new theme
    if (themeName !== 'default') {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      if (ENV_TYPE === 'local') {
        link.href = `/themes/${themeName}.css`;
      } else {
        link.href = BASE_NAME + `/themes/${themeName}.css`;
      }
      link.setAttribute('data-theme', themeName);

      // Add error handler to debug loading issues
      link.onerror = () => {
        console.error(`Failed to load theme: /themes/${themeName}.css`);
        alert(
          `Theme file not found: /themes/${themeName}.css\nMake sure the file exists in the public/themes/ folder`
        );
      };

      link.onload = () => {
        console.log(`Theme loaded successfully: ${themeName}`);
      };

      document.head.appendChild(link);
    }

    if (fixedEntryTheme.current === undefined) {
      localStorage.setItem('appTheme', themeName);
    }
  };

  const setTheme = (themeName: string) => {
    setCurrentTheme(themeName);
    applyTheme(themeName);
  };

  return (
    <ThemeContext.Provider value={{ currentTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
